import dayjs from "dayjs";
import { AssignmentTraceDTO } from "../../HubSpot/useGetSchedulerAssignments.ts";
import { classNames } from "../../UI/Utils/classNames.ts";
import {
  mostNotableOutcome,
  OUTCOME_CELL_CLASS_NAMES,
  OUTCOME_LABELS,
} from "./assignmentOutcome.ts";

const TZ = "Europe/Rome";
const HALF_HOUR = 30;
const FIRST_SLOT_MINUTE = 6 * 60;
const LAST_SLOT_MINUTE = 22 * 60 - HALF_HOUR;

export type SlotSelection = { label: string; traces: AssignmentTraceDTO[] };

type Props = {
  traces: AssignmentTraceDTO[];
  fromDay: string;
  toDay: string;
  showEmptyFutureDays: boolean;
  onSelect: (selection: SlotSelection) => void;
};

function minuteOfDay(iso: string): number {
  const time = dayjs(iso).tz(TZ);
  return time.hour() * 60 + time.minute();
}

function timeLabel(minute: number): string {
  return `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;
}

function daysBetween(fromDay: string, toDay: string): string[] {
  const days: string[] = [];
  for (
    let day = dayjs(fromDay);
    !day.isAfter(dayjs(toDay), "day");
    day = day.add(1, "day")
  ) {
    days.push(day.format("YYYY-MM-DD"));
  }
  return days;
}

export function AssignmentsHeatmap({
  traces,
  fromDay,
  toDay,
  showEmptyFutureDays,
  onSelect,
}: Props) {
  if (traces.length === 0) {
    return (
      <p className="text-sm text-slate-400">
        Nessuna assegnazione registrata nel periodo.
      </p>
    );
  }

  const byCell = new Map<string, AssignmentTraceDTO[]>();
  for (const trace of traces) {
    const day = dayjs(trace.slotStart).tz(TZ).format("YYYY-MM-DD");
    const slotMinute =
      Math.floor(minuteOfDay(trace.slotStart) / HALF_HOUR) * HALF_HOUR;
    const key = `${day}|${slotMinute}`;
    byCell.set(key, [...(byCell.get(key) ?? []), trace]);
  }
  const minutes = traces.map(
    (t) => Math.floor(minuteOfDay(t.slotStart) / HALF_HOUR) * HALF_HOUR,
  );
  const first = Math.min(FIRST_SLOT_MINUTE, ...minutes);
  const last = Math.max(LAST_SLOT_MINUTE, ...minutes);
  const columns = Array.from(
    { length: (last - first) / HALF_HOUR + 1 },
    (_, i) => first + i * HALF_HOUR,
  );
  const today = dayjs().tz(TZ).format("YYYY-MM-DD");
  const daysWithTraces = new Set(
    traces.map((t) => dayjs(t.slotStart).tz(TZ).format("YYYY-MM-DD")),
  );
  const rows = daysBetween(fromDay, toDay).filter(
    (day) => showEmptyFutureDays || day <= today || daysWithTraces.has(day),
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3 text-xs">
        {(
          [
            "normal",
            "requested",
            "cap_skipped",
            "tier1_overflow",
            "last_resort",
          ] as const
        ).map((outcome) => (
          <span key={outcome} className="flex items-center gap-1.5">
            <span
              className={classNames(
                "inline-block w-3.5 h-3.5 rounded",
                OUTCOME_CELL_CLASS_NAMES[outcome],
              )}
            />
            {OUTCOME_LABELS[outcome]}
          </span>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="text-xs border-separate border-spacing-0.5">
          <thead>
            <tr>
              <th />
              {columns.map((minute) => (
                <th
                  key={minute}
                  className="font-normal text-slate-400 w-10 whitespace-nowrap"
                >
                  {minute % 60 === 0 ? timeLabel(minute) : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((day) => (
              <tr key={day}>
                <td className="pr-2 text-slate-500 whitespace-nowrap">
                  {dayjs(day).locale("it").format("ddd DD/MM")}
                </td>
                {columns.map((minute) => {
                  const cell = byCell.get(`${day}|${minute}`) ?? [];
                  if (cell.length === 0) {
                    return (
                      <td
                        key={minute}
                        className="h-7 w-10 rounded bg-slate-50"
                      />
                    );
                  }
                  const outcome = mostNotableOutcome(cell);
                  const label = `${dayjs(day).locale("it").format("dddd DD/MM")} · ${timeLabel(minute)}–${timeLabel(minute + HALF_HOUR)}`;
                  return (
                    <td key={minute} className="h-7 w-10 p-0">
                      <button
                        type="button"
                        title={`${label} · ${cell.length} assegnazioni · ${OUTCOME_LABELS[outcome]}`}
                        onClick={() => onSelect({ label, traces: cell })}
                        className={classNames(
                          "h-7 w-10 rounded font-semibold tabular-nums",
                          OUTCOME_CELL_CLASS_NAMES[outcome],
                        )}
                      >
                        {cell.length}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
